import { useState } from 'react'
import { ItemEditor } from '../inventory/ItemEditor'
import { EmptyState, Panel } from '../primitives'
import { navigate } from '../router'
import { StructureDiagram } from './StructureDiagram'
import { StructureInspector } from './StructureInspector'
import { StructureOutline } from './StructureOutline'
import { StructureSummary } from './StructureSummary'
import { TreeIcon } from './treeIcons'
import { useStructure } from './useStructure'
import type { StructureDraft } from './useStructure'
import { useTreeState } from './useTreeState'

/**
 * "How it's made": what one item is made of, all the way down, against what is
 * on the shelf.
 *
 * The embeddable piece. Design › How it's made wraps it with a root picker; an
 * assembly's own page can drop it in as "Made of" with `compact`. Everything it
 * needs it reads itself (`useStructure`), so an embed is one line:
 *
 *     <StructureTree rootTypeId={item.id} compact />
 *
 * Two views of one state (`useTreeState`) — a node-link diagram and an
 * indented outline. The outline is the default on a phone, where a list held
 * in one hand reads better than a canvas; the diagram is one tap away.
 */
export function StructureTree({
  rootTypeId,
  compact = false,
  quantity = 1,
  onReroot,
  draft,
}: {
  rootTypeId: string
  compact?: boolean
  /** How many of the root to build — scales every need. */
  quantity?: number
  /** Offered as "Draw from here" on an assembly when present. */
  onReroot?: (typeId: string) => void
  /** An unsaved parts list to draw instead — the item editor's live preview. */
  draft?: StructureDraft
}): JSX.Element {
  const data = useStructure(rootTypeId, quantity, draft)
  const tree = useTreeState(data.view?.root ?? null)
  const [mode, setMode] = useState<'diagram' | 'outline'>(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 599px)').matches ? 'outline' : 'diagram',
  )
  const [editing, setEditing] = useState<string | null>(null)

  const { view } = data
  const { shown, chosen } = tree
  if (data.status === 'loading') return <span className="muted">Loading…</span>
  if (data.status === 'error') return <EmptyState title="Could not load the parts lists">Try again in a moment.</EmptyState>
  if (view === null || shown === null || data.status !== 'ready') {
    return (
      <Panel>
        <EmptyState icon="catalog" title={data.type ? `${data.type.name} is not made of anything here` : 'No such item'}>
          {data.type?.kind === 'buy'
            ? 'It is bought in whole, so there is nothing beneath it to draw.'
            : 'Give it a parts list — open the item and add what it is made of — and its tree appears here.'}
        </EmptyState>
      </Panel>
    )
  }

  const imageFor = (typeId: string): string | null => data.byId.get(typeId)?.imageId ?? null
  const views = {
    shown,
    selected: tree.selected,
    stockKnown: data.stockKnown,
    imageFor,
    onSelect: tree.select,
    onToggle: tree.toggle,
    onMove: tree.move,
  }

  return (
    <div className={`st${compact ? ' st--compact' : ''}`}>
      <StructureSummary
        view={view}
        buildable={data.buildable}
        quantity={quantity}
        stockKnown={data.stockKnown}
        compact={compact}
        imageFor={imageFor}
        onPick={tree.pick}
      />

      <div className="toolbar st-toolbar">
        <div className="segmented" role="tablist" aria-label="How to draw it">
          <button type="button" role="tab" aria-selected={mode === 'diagram'} onClick={() => setMode('diagram')}>
            <TreeIcon name="diagram" /> Diagram
          </button>
          <button type="button" role="tab" aria-selected={mode === 'outline'} onClick={() => setMode('outline')}>
            <TreeIcon name="outline" /> Outline
          </button>
        </div>
        <span className="toolbar__spacer" />
        <button type="button" aria-label="Expand all" onClick={tree.expandAll}>
          <TreeIcon name="expand" />
          <span className="st-toolbar__text">Expand all</span>
        </button>
        <button type="button" aria-label="Collapse all" onClick={tree.collapseAll}>
          <TreeIcon name="collapse" />
          <span className="st-toolbar__text">Collapse</span>
        </button>
      </div>

      <div className={`st-stage st-stage--${mode}${chosen ? ' st-stage--inspecting' : ''}`}>
        {mode === 'diagram' ? (
          <StructureDiagram
            {...views}
            resetKey={view.root.key}
            compact={compact}
            rootNote={data.buildable === null ? null : `Shelf builds ${data.buildable >= 99 ? '99+' : data.buildable} now`}
          />
        ) : (
          <StructureOutline {...views} />
        )}
        {chosen ? (
          <StructureInspector
            node={chosen.node}
            parentName={chosen.parent?.typeName ?? null}
            rootName={view.root.typeName}
            quantity={quantity}
            type={data.byId.get(chosen.node.typeId)}
            stockKnown={data.stockKnown}
            canEdit={data.canEdit}
            onClose={() => tree.select(null)}
            // A preview's own root is not saved yet, and leaving would lose the form.
            onOpenItem={draft ? null : () => navigate({ name: 'item', id: chosen.node.typeId })}
            onEdit={() => setEditing(chosen.node.typeId)}
            onReroot={
              onReroot && chosen.node.depth > 0 && chosen.node.children.length > 0
                ? () => {
                    tree.select(null)
                    onReroot(chosen.node.typeId)
                  }
                : null
            }
          />
        ) : null}
      </div>
      {mode === 'diagram' ? (
        <p className="st-hint">Drag to move · scroll or pinch to zoom · tab to a card, then arrow keys walk the tree.</p>
      ) : null}

      {editing !== null ? <ItemEditor typeId={editing} onClose={() => setEditing(null)} onSaved={() => setEditing(null)} /> : null}
    </div>
  )
}
